/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2019, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import { getSnapshot } from 'mobx-state-tree';
import { Formatter } from './FormatterWrapper';

const NA = '-';

export const CalcType = {
	TOTAL: 'total',
	TOTAL_SEL: 'totalSelection',
	MIN_SEL: 'minSelection',
	MAX_SEL: 'maxSelection',
	DIFF: 'difference',
	DIFF_SEL: 'differenceSelection',
	PERCENT_vsALLDATA: 'percentVsAllData',
	PERCENT_SEL_vsALLDATA: 'percentSelVsAllData',
	PERCENT_CHARTvsCHART: 'percentChartVsChart',
	PERCENT_SELvsCHART: 'percentSelVsChart',
	MIN: 'min',
	MAX: 'max',
	AVG: 'avg',
	AVG_SEL: 'avgSelection',
	WEIGHTED_AVG: 'weightedAvg',
	WEIGHTED_AVG_SEL: 'weightedAvgSelection',
	DETAIL_ROWCOUNT: 'detailRowCount',
	DETAIL_SUM: 'detailSum',
	SEL_CONTEXT: 'selectionContext',
	NUM_SELECTIONS: 'numSelections',
	HAS_NEGATIVE_VALUES: 'hasNegativeValues',
	MISSING_VALUE_COUNT: 'missingValueCount',
	ROW_COUNT: 'rowCount',
	SUMMARY_USE: 'summaryUse'
};

//Get a single tooltip for a compare table column from string resources based on the calculation and an optional line#.
//If a missingValueCount is included, both the 'pure tooltip' is included (for no missingValueCount or missingValueCount=0)
//AND a 'missingValueCount' id should exist in exploreResources (that reports the missing values in some way)
//Form: compareTable_tip_<aggtype>{_n}{_missingValueCount}
function _getCompareTableTip(getStringRes, calc, params, lineNumber) {
	const lineNumberStr = (lineNumber === undefined) ? '' : ('_' + lineNumber);
	const missingValueCountStr = (params.missingValueCount && params.missingValueCount > 0) ? '_missingValueCount' : '';
	const toolTipId = 'compareTable_tip_' + calc + lineNumberStr + missingValueCountStr;
	return getStringRes(toolTipId, params);
}

function _getToolTipsList(getStringRes, num, calc, params) {
	if (getStringRes) {
		const tips = [];
		for (let i = 0; i < num; i++) {
			tips[i] = _getCompareTableTip(getStringRes, calc, params, i);
		}
		return tips;
	}
	return undefined;
}

//Get a single tooltip for the combined column of the compare table from string resources based on the calculation and an optional line#.
function _getCombinationToolTip(getStringRes, calc, params, lineNumber) {
	const lineNumberStr = (lineNumber === undefined) ? '' : ('_' + lineNumber);
	return getStringRes('compareTable_tip_combination_' + calc + lineNumberStr, params);
}

function _getCombinationToolTipsList(getStringRes, num, calc, params) {
	if (getStringRes) {
		const tips = [];
		for (let i = 0; i < num; i++) {
			tips[i] = _getCombinationToolTip(getStringRes, calc, params, i);
		}
		return tips;
	}
	return undefined;
}

function _getCombinedCalcTooltip(getString, allMeasuresSame, widgetContextCheckResult) {
	if (getString) {
		const tips = [];
		if (!allMeasuresSame) {
			tips[0] = getString('compareTable_tip_na_incompatible_measures');
		} else if (widgetContextCheckResult && !widgetContextCheckResult.canCombineCalculations) {
			tips[0] = getString('compareTable_tip_na_overlap');
		} else if (widgetContextCheckResult && !widgetContextCheckResult.isCategoryContextSame) {
			tips[0] = getString('compareTable_tip_na_category_context');
		}
		return tips;
	}
	return undefined;
}

function _round(num) {
	let rounded = Math.round(num * 100) / 100;
	if (rounded === 0) {
		rounded = Math.round(num * 1000) / 1000;
	}
	if (rounded === 0) {
		rounded = Math.round(num * 10000) / 10000;
	}
	return rounded;
}

function _formatNumber(theNumber, formatSpec) {
	if (isNaN(theNumber) || theNumber === NA) {
		return NA;  //The string equivalent to undefined is NA (-)
	} else if (typeof (formatSpec) !== 'undefined') {
		return Formatter.format(theNumber, formatSpec);
	}
	return theNumber;   //Its not a number....just return what was passed in.
}

function _formatPercent(theNumber, formatSpec) {
	if (formatSpec) {
		//Use the locale from the formatSpec to determine how to format the number as a %age.
		const formatSpecForPercent = { locale: formatSpec.locale };
		return _formatNumber(theNumber, formatSpecForPercent);
	}
	return theNumber;
}

function _percentString(num) {
	if (typeof (num) === 'number') {
		return isNaN(num) ? num : num + '%';
	} else {
		return num !== 'NaN' && num !== NA ? num + '%' : NA;
	}
}

function _isMinMax(aggType) {
	return (aggType === 'min' || aggType === 'max');
}

function _isMin(aggType) {
	return (aggType === 'min');
}

function _isMax(aggType) {
	return (aggType === 'max');
}

function _isAvg(aggType) {
	return (aggType === 'avg');
}

function _isCountDistinct(aggType) {
	return (aggType === 'countdistinct');
}

function _isSummable(aggType) {
	return (aggType && (aggType === 'sum' || aggType === 'count'));
}

function _supportsAverage(aggType) {
	//Averages are supported for summables (sum/count) and minMax.
	return _isMinMax(aggType) || _isSummable(aggType);
}

function _supportsWeightedAverage(aggType) {
	//Weighted Averages are supported for avg only (currently)
	return _isAvg(aggType);
}


function _checkAggregateType(measures, fn) {
	if (measures.length === 0) {
		return false;
	}
	let agg = true;
	measures.forEach((measure) => {
		const meta = measure && measure.metadata && measure.metadata.aggregationType;
		agg = agg && fn(meta);
	});
	return agg;
}

function _atleastOneAggType(measures, fn) {
	if (measures.length === 0) {
		return false;
	}
	const agg = measures.some((measure) => {
		const meta = measure && measure.metadata && measure.metadata.aggregationType;
		return fn(meta);
	});
	return agg;
}

function _atleastOneAggTypeSupportsAverages(measures) {
	//Simple averages are valid for min/max/count/sum.
	return _atleastOneAggType(measures, _isSummable) || _atleastOneAggType(measures, _isMinMax);
}

function _addAverage(stats, measures = null) {
	//This function appends average to the supplied stats array
	//If measures is passed in, the calc is only appended if the check for applicable types passes.
	if (!measures || _atleastOneAggTypeSupportsAverages(measures)) {
		stats.push(CalcType.AVG);
	}
}

function _addAverageSel(stats, measures = null) {
	//This function appends average to the supplied stats array (selection case)
	//If measures is passed in, the calc is only appended if the check for applicable types passes.
	if (!measures || _atleastOneAggTypeSupportsAverages(measures)) {
		stats.push(CalcType.AVG_SEL);
	}
}

function _checkAggregateTypeGeneric(entries, fn) {
	if (entries.length === 0) {
		return false;
	}
	let agg = true;
	entries.forEach((measure) => {
		const meta = measure.aggregationType;
		agg = agg && fn(meta);
	});
	return agg;
}


function _areAllMeasuresSame(statsInfoList) {
	let isInCompatible = false;
	if (statsInfoList.length === 0) {
		return false;
	}

	let name = undefined;
	isInCompatible = statsInfoList.some((statsInfo) => {
		if (!name) {
			name = statsInfo.measureName;
			return false;
		} else {
			return (name !== statsInfo.measureName);
		}
	});
	return !isInCompatible;
}

function _oneOrMoreRowCountsGreaterThanZero(statsInfoList) {
	return statsInfoList
		.some((statsInfo) => {
			const stats = statsInfo.statsUse;
			const rowCount = stats[CalcType.ROW_COUNT];
			return rowCount > 0;
		});
}

function _getMeasureValue(aggType, valueArray) {
	// the total used for _isMin(aggType) || _isMax(aggType) to compute the average is not displayed directly and is not applicable calculation
	if (_isSummable(aggType) || _isMin(aggType) || _isMax(aggType)) {
		if (valueArray && isNaN(valueArray)) {
			return _getValueTotal(valueArray);
		} else {
			return valueArray || 0;
		}
	} else if (_isAvg(aggType)) {
		return valueArray;
	} else {
		return undefined;
	}
}

function _getValueCount(measureDetail) {
	// to match predict we discount nulls and other non numeric values
	return measureDetail && measureDetail['values'].filter((e) => { return typeof e === 'number'; }).length || 0;
}

function _getMissingValueCount(measureDetail) {
	// for selection stats we count nulls as missing values
	return measureDetail && measureDetail['values'].filter((e) => { return e === null; }).length || 0;
}

function _getValueTotal(valueArray) {
	return valueArray.length > 0 && valueArray.reduce((accumulator, currentValue) => accumulator + currentValue) || 0;
}

/***********************
 * Compare table stats
 ***********************/

function getChartSummaryStats(options) {
	const { newCompareTableStatsEnabled, statsModel, chartVsChart, measureName, getString, widgetAlias } = options;

	const getTooltipList = function (...theArgs) {
		return getString && _getToolTipsList(getString, ...theArgs) || '';
	};

	const stats = {};
	const statsUse = {};
	const toolTip = {};

	//Formatted strings reported in the compare table.
	let min = NA;
	let max = NA;
	let avg = NA;
	let weightedAvg = NA;
	let difference;
	let total = NA;
	let percentVsAllData = NA;
	let percentChartVsChart = NA;

	//Unformatted values.
	let totalUse, minUse, maxUse, avgUse, rowCount, weightedAvgUse, detailSumUse, detailRowCountUse, missingValueCount;

	//Calculated strings
	let percentVsAllData_f = percentVsAllData;
	let percentChartVsChart_f = percentChartVsChart;

	//Tooltips
	let percentVsAllDataTip = '', percentChartVsChartTip = '', totalTip = '';

	const measuresMap = statsModel.measures || new Map();
	const measureDetail = measuresMap.get(measureName);
	const meta = measureDetail && measureDetail.get('metadata');
	const hasNegativeValues = measureDetail && measureDetail.get('hasNegativeValues') || false;

	const aggregationType = meta && meta.aggregationType;
	const summaryUse = measureDetail && measureDetail.get('summaryValueUse');

	if (measureDetail) {
		min = measureDetail.get('min');
		max = measureDetail.get('max');
		avg = measureDetail.get('avg');
		weightedAvg = measureDetail.get('weightedAvg') || NA;
		minUse = measureDetail.get('minUse');
		maxUse = measureDetail.get('maxUse');
		avgUse = measureDetail.get('avgUse');
		rowCount = measureDetail.get('rowCount');

		//Note: The formatted "total" should be N/A UNLESS the aggregation type is summable (sum/count) so don't save it otherwise
		//However: save totalUse into the statsUse table so that it can be used to calculate averages of min and max (see _computeAverageData()).
		totalUse = measureDetail.get('totalUse');

		weightedAvgUse = measureDetail.get('weightedAvgUse');
		//These 2 are intermediate use values for calculation a combined detail average.
		detailSumUse = measureDetail.get('detailSumUse');
		detailRowCountUse = measureDetail.get('detailRowCountUse');
		missingValueCount = measureDetail.get('missingValueCount');
	}

	if (meta) {
		if (_isSummable(meta.aggregationType)) {
			total = measureDetail.get('total');

			let summaryUse_f = _formatNumber(summaryUse, formatSpec);

			percentChartVsChart = chartVsChart && chartVsChart[measureName] && chartVsChart[measureName].value;
			percentVsAllData = _round(totalUse / summaryUse * 100);

			let allChartTotal = chartVsChart && chartVsChart[measureName] && chartVsChart[measureName].allChartTotal;

			const formatSpec = meta.format;
			total = total || _formatNumber(totalUse, formatSpec);
			summaryUse_f = _formatNumber(summaryUse_f, formatSpec);
			allChartTotal = _formatNumber(allChartTotal, formatSpec);

			percentVsAllData_f = _formatPercent(percentVsAllData, formatSpec);
			percentChartVsChart_f = _formatPercent(percentChartVsChart, formatSpec);
			//Present a tooltip if this total has negative values.
			totalTip = hasNegativeValues && getTooltipList(2, CalcType.TOTAL, { itemName: meta.label, widgetAlias });
			// Show tooltips only when formatted total use, summary use and allChartTotal are defined
			if (typeof total !== NA && summaryUse_f !== NA && typeof allChartTotal !== 'undefined') {
				percentChartVsChartTip = getTooltipList(2, CalcType.PERCENT_CHARTvsCHART, { widgetAlias, total: total, allChartTotal });
				percentVsAllDataTip = getTooltipList(hasNegativeValues ? 3 : 2, CalcType.PERCENT_vsALLDATA, { widgetAlias, total: total, summary: summaryUse_f });
			}
		} else if (newCompareTableStatsEnabled && _isCountDistinct(aggregationType)) {
			totalUse = measureDetail.get('filteredSummaryValueUse');
			total = measureDetail.get('filteredSummaryValue');
			if (totalUse !== undefined && summaryUse !== undefined) {
				const formatSpec = meta.format;
				percentVsAllData = _round(totalUse / summaryUse * 100);
				percentVsAllData_f = _formatPercent(percentVsAllData, formatSpec);
				const summaryUse_f = _formatNumber(summaryUse, formatSpec);
				totalTip = _getCompareTableTip(getString, aggregationType + '_' + CalcType.TOTAL, { uniqueValues: total, label: meta.label, overallSummaryValue: summaryUse_f });
				percentVsAllDataTip = _getCompareTableTip(getString, aggregationType + '_' + CalcType.PERCENT_vsALLDATA, { uniqueValues: total, label: meta.label, percent: _percentString(percentVsAllData_f), overallSummaryValue: summaryUse_f });
			}
		}
	}

	const supportsAvg = _supportsAverage(aggregationType) && (avg || avg === 0);
	//all stats for selection are NA
	stats[CalcType.TOTAL] = (total || total === 0) ? total : NA;
	stats[CalcType.TOTAL_SEL] = NA;
	stats[CalcType.MIN_SEL] = NA;
	stats[CalcType.MAX_SEL] = NA;
	stats[CalcType.DIFF] = difference;
	stats[CalcType.DIFF_SEL] = undefined;
	stats[CalcType.PERCENT_vsALLDATA] = percentVsAllData_f && _percentString(percentVsAllData_f) || NA;
	stats[CalcType.PERCENT_CHARTvsCHART] = percentChartVsChart_f && _percentString(percentChartVsChart_f) || NA;
	stats[CalcType.PERCENT_SELvsCHART] = NA;
	stats[CalcType.MIN] = typeof min !== 'undefined' ? min.toString() : NA;
	stats[CalcType.MAX] = typeof max !== 'undefined' ? max.toString() : NA;
	stats[CalcType.AVG] = supportsAvg ? avg.toString() : NA;
	stats[CalcType.WEIGHTED_AVG] = _supportsWeightedAverage(aggregationType) ? weightedAvg : NA;
	stats[CalcType.NUM_SELECTIONS] = NA;
	stats[CalcType.ROW_COUNT] = rowCount || NA;
	stats[CalcType.MISSING_VALUE_COUNT] = missingValueCount || NA;

	statsUse[CalcType.TOTAL] = (totalUse || totalUse === 0) ? totalUse : NA;
	statsUse[CalcType.DIFF] = 0;
	statsUse[CalcType.PERCENT_vsALLDATA] = percentVsAllData;
	statsUse[CalcType.PERCENT_CHARTvsCHART] = percentChartVsChart;
	statsUse[CalcType.PERCENT_SELvsCHART] = NA;
	statsUse[CalcType.MIN] = (minUse || minUse === 0) ? minUse : NA;
	statsUse[CalcType.MAX] = (maxUse || maxUse === 0) ? maxUse : NA;
	statsUse[CalcType.AVG] = _supportsAverage(aggregationType) ? avgUse : NA;
	statsUse[CalcType.ROW_COUNT] = rowCount;
	statsUse[CalcType.MISSING_VALUE_COUNT] = missingValueCount;

	statsUse[CalcType.WEIGHTED_AVG] = _supportsWeightedAverage(aggregationType) ? weightedAvgUse : NA;
	statsUse[CalcType.DETAIL_ROWCOUNT] = detailRowCountUse;
	statsUse[CalcType.DETAIL_SUM] = detailSumUse;
	statsUse[CalcType.NUM_SELECTIONS] = 0;
	statsUse[CalcType.SUMMARY_USE] = summaryUse;

	if (hasNegativeValues && aggregationType === 'sum') {
		//If the stats returns that there are negative values AND this is a summed item, mark BOTH the TOTAL and PERCENT_vsALLDATA cells (and augment tooltips)
		statsUse[CalcType.HAS_NEGATIVE_VALUES] = [CalcType.TOTAL, CalcType.PERCENT_vsALLDATA];
	}

	toolTip[CalcType.PERCENT_vsALLDATA] = percentVsAllDataTip;
	toolTip[CalcType.PERCENT_CHARTvsCHART] = percentChartVsChartTip;
	if (totalTip) {
		toolTip[CalcType.TOTAL] = totalTip;
	}
	if (stats[CalcType.WEIGHTED_AVG] !== NA) {
		toolTip[CalcType.WEIGHTED_AVG] = _getCompareTableTip(getString, CalcType.WEIGHTED_AVG, { weightedAvg: weightedAvg, detailRowCount: detailRowCountUse, missingValueCount: missingValueCount });
	}
	if (stats[CalcType.AVG] !== NA) {
		toolTip[CalcType.AVG] = _getCompareTableTip(getString, CalcType.AVG, { avg: avg, rowCount: rowCount, missingValueCount: missingValueCount });
	}

	return { stats, statsUse, toolTip, aggregationType, measureName, meta, widgetAlias };
}


/***********************
 * Compare table selection stats
 ***********************/

function getSelectionContextLabel(selStatsModel, measureName) {
	const lastCategorySelection = selStatsModel.lastCategorySelection && getSnapshot(selStatsModel.lastCategorySelection) || [];

	const measuresMap = selStatsModel.measures || new Map();
	const measureDetail = measuresMap.get(measureName);
	const numSelections = _getValueCount(measureDetail);

	const lastCategorySelectionLabel = lastCategorySelection.length > 1 ? lastCategorySelection.join(' | ') : lastCategorySelection[0] || '';
	return { lastCategorySelectionLabel, numSelections };
}

function getChartSelectionSummaryStats(options) {
	const { newCompareTableStatsEnabled, statsModel, selStatsModel, measureName, getString, widgetAlias } = options;

	const getTooltipList = function (...theArgs) {
		return getString && _getToolTipsList(getString, ...theArgs) || '';
	};

	const stats = {};
	const statsUse = {};
	const toolTip = {};

	let min_f = NA;
	let max_f = NA;
	let avg_f = NA;
	let weightedAvg_f = NA;
	let totalUse_f = NA;
	let minSelection;
	let maxSelection;
	let hasNegativeValues = false;
	let difference;
	let percentVsAllData = NA;
	let percentSelVsChart = NA;
	let defTotalUse, avgUse, weightedAvgUse, detailRowCountUse, detailSumUse;
	let percentVsAllData_f = percentVsAllData, percentSelVsChart_f = percentSelVsChart;

	let percentVsAllDataTip = '', percentSelVsChartTip = '', totalSelTip = '';

	const defMeasuresMap = statsModel.measures || new Map();
	const defMeasureDetail = defMeasuresMap.get(measureName);
	const meta = defMeasureDetail && defMeasureDetail.get('metadata');

	const measuresMap = selStatsModel.measures || new Map();
	const measureDetail = measuresMap.get(measureName);

	const lastCategorySelection = selStatsModel.lastCategorySelection && getSnapshot(selStatsModel.lastCategorySelection) || [];
	const formatSpec = meta && meta.format;

	const aggregationType = meta && meta.aggregationType;

	let value;
	const numSelections = _getValueCount(measureDetail);
	const rowCount = _getValueCount(measureDetail);
	const missingValueCount = _getMissingValueCount(measureDetail);

	let lastCategorySelectionLabel = lastCategorySelection.length > 1 ? lastCategorySelection.join(' | ') : lastCategorySelection;
	if (numSelections > 1) {
		lastCategorySelectionLabel = lastCategorySelectionLabel + '+' + (numSelections - 1);
	}

	let totalUse = _getMeasureValue(aggregationType, measureDetail && measureDetail['values']);
	totalUse = _round(totalUse);
	const summaryUse = defMeasureDetail && defMeasureDetail.get('summaryValueUse');

	if (meta && _isSummable(aggregationType)) {
		defTotalUse = defMeasureDetail.get('totalUse');
		hasNegativeValues = defMeasureDetail.get('hasNegativeValues');
		percentSelVsChart = _round(totalUse / defTotalUse * 100);
		percentVsAllData = _round(totalUse / summaryUse * 100);

		const formatSpec = meta.format;
		let defTotalUse_f = defTotalUse, summaryUse_f = summaryUse;
		totalUse_f = _formatNumber(totalUse, formatSpec);
		summaryUse_f = _formatNumber(summaryUse_f, formatSpec);
		defTotalUse_f = _formatNumber(defTotalUse_f, formatSpec);

		percentVsAllData_f = _formatPercent(percentVsAllData, formatSpec);
		percentSelVsChart_f = _formatPercent(percentSelVsChart, formatSpec);
		totalSelTip = hasNegativeValues && getTooltipList(2, CalcType.TOTAL_SEL, { itemName: meta.label, widgetAlias });
		percentSelVsChartTip = getTooltipList(2, CalcType.PERCENT_SELvsCHART, { widgetAlias, total: totalUse_f, summary: defTotalUse_f });
		percentVsAllDataTip = getTooltipList(hasNegativeValues ? 3 : 2, CalcType.PERCENT_SEL_vsALLDATA, { widgetAlias, total: totalUse_f, summary: summaryUse_f });
	}
	//Report min and max as actual values (even for the summables) if the combination of aggregation types makes sense.
	const allValues = measureDetail && measureDetail['values'] || [];
	maxSelection = Math.max(...allValues);
	maxSelection = _round(maxSelection);
	let maxSelection_f = maxSelection;
	minSelection = Math.min(...allValues);
	minSelection = _round(minSelection);
	let minSelection_f = minSelection;

	if (formatSpec) {
		maxSelection_f = _formatNumber(maxSelection_f, formatSpec);
		minSelection_f = _formatNumber(minSelection_f, formatSpec);
	}

	if (meta && (_isMin(aggregationType) || _isCountDistinct(aggregationType))) {
		min_f = _formatNumber(value, formatSpec);
	}

	if (meta && (_isMax(aggregationType) || _isCountDistinct(aggregationType))) {
		max_f = _formatNumber(value, formatSpec);
	}

	if (newCompareTableStatsEnabled && meta && (_isCountDistinct(aggregationType))) {
		totalUse = measureDetail && measureDetail.filteredSummaryValueUse;
		totalUse_f = _formatNumber(totalUse, formatSpec);

		percentVsAllData = _round(totalUse / summaryUse * 100);
		percentVsAllData_f = _formatPercent(percentVsAllData, formatSpec);
		if (totalUse !== undefined && summaryUse !== undefined) {
			const formatSpec = meta.format;
			const summaryUse_f = _formatNumber(summaryUse, formatSpec);
			totalSelTip = _getCompareTableTip(getString, aggregationType + '_' + CalcType.TOTAL_SEL, { uniqueValues: totalUse_f, label: meta.label, overallSummaryValue: summaryUse_f });
			percentVsAllDataTip = _getCompareTableTip(getString, aggregationType + '_' + CalcType.PERCENT_SEL_vsALLDATA, { uniqueValues: totalUse_f, label: meta.label, percent: _percentString(percentVsAllData_f), overallSummaryValue: summaryUse_f });
		}
	}

	if (meta && _supportsWeightedAverage(aggregationType)) {
		if (measureDetail && measureDetail.weightedAvgUse !== undefined && measureDetail.detailRowCountUse > 0) {
			weightedAvg_f = formatSpec ? _formatNumber(measureDetail.weightedAvgUse, formatSpec) : measureDetail.weightedAvgUse.toString();
			weightedAvgUse = measureDetail.weightedAvgUse;
			detailRowCountUse = measureDetail.detailRowCountUse;
			detailSumUse = measureDetail.detailSumUse;
		}
	}

	if (meta && (_supportsAverage(aggregationType))) {
		const tempCountOfValues = _getValueCount(measureDetail);
		if (tempCountOfValues > 0) {
			avgUse = _getMeasureValue(aggregationType, measureDetail && measureDetail['values']) / tempCountOfValues;
			avg_f = formatSpec ? _formatNumber(avgUse, formatSpec) : avgUse.toString();
		}
	}

	//all stats for default summary are NA
	stats[CalcType.NUM_SELECTIONS] = _getValueCount(measureDetail);
	stats[CalcType.SEL_CONTEXT] = lastCategorySelectionLabel;

	stats[CalcType.TOTAL] = NA;
	stats[CalcType.DIFF] = difference;
	stats[CalcType.TOTAL_SEL] = totalUse_f;
	stats[CalcType.MIN_SEL] = minSelection_f;
	stats[CalcType.MAX_SEL] = maxSelection_f;
	stats[CalcType.DIFF_SEL] = difference;
	stats[CalcType.PERCENT_vsALLDATA] = NA;
	stats[CalcType.PERCENT_SEL_vsALLDATA] = percentVsAllData_f && _percentString(percentVsAllData_f) || NA;
	stats[CalcType.PERCENT_CHARTvsCHART] = NA;
	stats[CalcType.PERCENT_SELvsCHART] = percentSelVsChart_f && _percentString(percentSelVsChart_f) || NA;
	stats[CalcType.MIN] = min_f;
	stats[CalcType.MAX] = max_f;
	stats[CalcType.AVG] = avg_f;
	stats[CalcType.AVG_SEL] = avg_f;
	stats[CalcType.WEIGHTED_AVG_SEL] = weightedAvg_f;

	statsUse[CalcType.TOTAL_SEL] = totalUse;
	statsUse[CalcType.MIN_SEL] = minSelection;
	statsUse[CalcType.MAX_SEL] = maxSelection;
	statsUse[CalcType.DIFF_SEL] = 0;
	statsUse[CalcType.PERCENT_vsALLDATA] = NA;
	statsUse[CalcType.PERCENT_SEL_vsALLDATA] = percentVsAllData;
	statsUse[CalcType.PERCENT_CHARTvsCHART] = 0;
	statsUse[CalcType.PERCENT_SELvsCHART] = percentSelVsChart;
	statsUse[CalcType.MIN] = NA;
	statsUse[CalcType.MAX] = NA;
	statsUse[CalcType.AVG] = avgUse || NA;
	statsUse[CalcType.ROW_COUNT] = rowCount;
	statsUse[CalcType.MISSING_VALUE_COUNT] = missingValueCount;
	statsUse[CalcType.AVG_SEL] = avgUse || NA;
	statsUse[CalcType.WEIGHTED_AVG_SEL] = weightedAvgUse || NA;
	statsUse[CalcType.DETAIL_ROWCOUNT] = detailRowCountUse; //This information only surfaces in tooltips
	statsUse[CalcType.DETAIL_SUM] = detailSumUse;
	statsUse[CalcType.SUMMARY_USE] = summaryUse;

	if (hasNegativeValues && aggregationType === 'sum') {
		//If the stats returns that there are negative values, mark BOTH the TOTAL and PERCENT_SEL_vsALLDATA cells (and augment tooltips)
		statsUse[CalcType.HAS_NEGATIVE_VALUES] = [CalcType.TOTAL_SEL, CalcType.PERCENT_SEL_vsALLDATA];
	}

	statsUse[CalcType.NUM_SELECTIONS] = _getValueCount(measureDetail);

	toolTip[CalcType.PERCENT_SEL_vsALLDATA] = percentVsAllDataTip;
	toolTip[CalcType.PERCENT_SELvsCHART] = percentSelVsChartTip;
	if (totalSelTip) {
		toolTip[CalcType.TOTAL_SEL] = totalSelTip;
	}
	if (statsUse[CalcType.AVG_SEL] !== NA) {
		toolTip[CalcType.AVG_SEL] = _getCompareTableTip(getString, CalcType.AVG_SEL, { avg: avg_f, rowCount: rowCount, missingValueCount: missingValueCount });
	}
	if (statsUse[CalcType.WEIGHTED_AVG_SEL] !== NA) {
		toolTip[CalcType.WEIGHTED_AVG_SEL] = _getCompareTableTip(getString, CalcType.WEIGHTED_AVG_SEL, { weightedAvg: weightedAvg_f, detailRowCount: detailRowCountUse, missingValueCount: missingValueCount });
	}

	return { stats, statsUse, toolTip, aggregationType, measureName, meta, widgetAlias };
}

/***********************
 * compare table calculated stats
 ***********************/

/**
 * returns combined calculations
 * @param {object} params
 * @param {object[]} params.statsInfoList  each object is return value from getChartSummaryStats()
 *                                  ie { stats, statsUse, aggregationType, measureName, meta }
 * @param {string[]} params.applicableCalcs list of applicable calculations for all widgets on compare card
 *                                  e.g [ 'total','difference','percentVsAllData','percentChartVsChart' ]
 * @param {function} params.getString
 * @param {object} params.widgetContextCheckResult
 * @return {string[]} applicable stat values for all calcs in applicableCalcs
 */
function getCalculatedSummaryData(params) {
	const { newCompareTableStatsEnabled, statsInfoList, applicableCalcs, getString, widgetContextCheckResult, combinedSummaryValueMap } = params;
	let computedStatsAndTooltip = { stats: {}, statsUse: {}, toolTip: {} };

	const getTooltip = function (...theArgs) {
		return getString && _getCombinationToolTipsList(getString, ...theArgs) || '';
	};
	const getSingleTooltip = function (...theArgs) {
		return getString && _getCombinationToolTip(getString, ...theArgs) || '';
	};
	const applicableStatsList = [];
	const allMeasuresSame = _areAllMeasuresSame(statsInfoList);
	const oneOrMoreRowCountsGreaterThanZero = _oneOrMoreRowCountsGreaterThanZero(statsInfoList);
	const canDiffThisCountDistinctCalculation = newCompareTableStatsEnabled && _checkAggregateTypeGeneric(statsInfoList, _isCountDistinct);
	const isCalculationValid = allMeasuresSame
		&& oneOrMoreRowCountsGreaterThanZero
		&& (widgetContextCheckResult.canCombineCalculations || canDiffThisCountDistinctCalculation)
		&& widgetContextCheckResult.isCategoryContextSame
		&& statsInfoList.length > 1;

	if (isCalculationValid) {
		//This block returns a structure with any computations defined
		//{
		//  stats: { difference: 50, differenceSelection: 20, avg: 40, avgSelection: 10 },
		//  tooltip: { difference: '-', differenceSelection: '-', avg: 'cannot show total when data overlaps', avgSelection: '-'}
		//}
		if (_checkAggregateTypeGeneric(statsInfoList, _isSummable)) {
			computedStatsAndTooltip = _computeCalculatedDataForSummables(statsInfoList, getTooltip);
			computedStatsAndTooltip = _computeAverageData(statsInfoList, /*not used*/undefined, getSingleTooltip, computedStatsAndTooltip); // append to existing stats
		} else if (_checkAggregateTypeGeneric(statsInfoList, _isCountDistinct)) {
			//distinct counts can be subtracted (it simply means how many more distinct values are in 1 set than another).
			computedStatsAndTooltip = _computeDiffData(statsInfoList, CalcType.TOTAL, CalcType.TOTAL_SEL, getTooltip, /*multi*/true);
			if (allMeasuresSame && combinedSummaryValueMap) {
				const totalCalc = (statsInfoList[0].stats[CalcType.TOTAL_SEL] !== NA) ? CalcType.TOTAL_SEL : CalcType.TOTAL;
				const percentOfDatasetCalc = (statsInfoList[0].stats[CalcType.TOTAL_SEL] !== NA) ? CalcType.PERCENT_SEL_vsALLDATA : CalcType.PERCENT_vsALLDATA;

				const measureName = statsInfoList[0].measureName;
				computedStatsAndTooltip = _computeCombinedSummaryValues(
					combinedSummaryValueMap[measureName], totalCalc, percentOfDatasetCalc,
					statsInfoList[0].statsUse.summaryUse,
					getSingleTooltip, computedStatsAndTooltip);
			}
		} else if (_checkAggregateTypeGeneric(statsInfoList, _isAvg)) {
			computedStatsAndTooltip = _computeWeightedAverageData(statsInfoList, 'avg', getSingleTooltip);
		} else if (_checkAggregateTypeGeneric(statsInfoList, _isMax)) {
			computedStatsAndTooltip = _computeDiffData(statsInfoList, CalcType.MAX, CalcType.MAX_SEL, getTooltip);
			computedStatsAndTooltip = _computeAverageData(statsInfoList, CalcType.MAX, getSingleTooltip, computedStatsAndTooltip); // append to existing stats
		} else if (_checkAggregateTypeGeneric(statsInfoList, _isMin)) {
			computedStatsAndTooltip = _computeDiffData(statsInfoList, CalcType.MIN, CalcType.MIN_SEL, getTooltip);
			computedStatsAndTooltip = _computeAverageData(statsInfoList, CalcType.MIN, getSingleTooltip, computedStatsAndTooltip); // append to existing stats
		}
	}

	applicableCalcs.forEach((calc) => {
		const value = (isCalculationValid && computedStatsAndTooltip.stats[calc] !== undefined) ? computedStatsAndTooltip.stats[calc] : NA;
		const valueUse = (isCalculationValid && computedStatsAndTooltip.statsUse && computedStatsAndTooltip.statsUse[calc] !== undefined) ? computedStatsAndTooltip.statsUse[calc] : undefined;
		let toolTip = isCalculationValid && computedStatsAndTooltip.toolTip[calc];

		if (!isCalculationValid && oneOrMoreRowCountsGreaterThanZero) {
			toolTip = _getCombinedCalcTooltip(getString, allMeasuresSame, widgetContextCheckResult);
		}

		applicableStatsList.push({ value, valueUse, toolTip });
	});
	return applicableStatsList;
}

function _setSummableToolTips(toolTipStr, statsInfoList, getTooltip, formatSpec, isSelection) {
	const calcsList = [
		[CalcType.TOTAL, CalcType.DIFF],
		[CalcType.TOTAL_SEL, CalcType.DIFF_SEL]
	];

	const calcs = isSelection ? calcsList[1] : calcsList[0];

	const TOTAL = 0, DIFF = 1;
	const _roundAndFormatNum = (num) => {
		const rnum = _round(num);
		return formatSpec ? _formatNumber(rnum, formatSpec) : rnum.toString();
	};
	const a = {}, b = {};
	a[calcs[TOTAL]] = _roundAndFormatNum(statsInfoList[0].statsUse[calcs[TOTAL]]);
	b[calcs[TOTAL]] = _roundAndFormatNum(statsInfoList[1].statsUse[calcs[TOTAL]]);

	if (!isSelection) {
		a[CalcType.PERCENT_vsALLDATA] = _roundAndFormatNum(statsInfoList[0].statsUse[CalcType.PERCENT_vsALLDATA]);
		b[CalcType.PERCENT_vsALLDATA] = _roundAndFormatNum(statsInfoList[1].statsUse[CalcType.PERCENT_vsALLDATA]);
	}

	toolTipStr[calcs[DIFF]] = getTooltip(2, calcs[DIFF], { a: a[calcs[TOTAL]], b: b[calcs[TOTAL]] });
	toolTipStr[calcs[TOTAL]] = getTooltip(2, calcs[TOTAL], { a: a[calcs[TOTAL]], b: b[calcs[TOTAL]] });
	if (!isSelection) {
		toolTipStr[CalcType.PERCENT_vsALLDATA] = getTooltip(2, CalcType.PERCENT_vsALLDATA, { a: a[CalcType.PERCENT_vsALLDATA], b: b[CalcType.PERCENT_vsALLDATA] });
	}
}

function _setDiffToolTips(toolTipStr, prop, statsInfoList, getTooltip, formatSpec, isSelection) {

	const _roundAndFormatNum = (num) => {
		const rnum = _round(num);
		return formatSpec ? _formatNumber(rnum, formatSpec) : rnum.toString();
	};
	const a = {}, b = {};
	a[prop] = _roundAndFormatNum(statsInfoList[0].statsUse[prop]);
	b[prop] = _roundAndFormatNum(statsInfoList[1].statsUse[prop]);

	const calc = isSelection ? CalcType.DIFF_SEL : CalcType.DIFF;
	toolTipStr[calc] = getTooltip(2, calc, { a: a[prop], b: b[prop] });
}

function _computeCalculatedDataForSummables(statsInfoList, getTooltip) {
	const result = {};
	const resultUse = {};
	const toolTip = {};

	let total = 0;
	let diff = undefined;
	let perAllData = 0;

	const toolTipStr = {};

	const formatSpec = statsInfoList[0].meta && statsInfoList[0].meta.format;

	if (statsInfoList && statsInfoList.length > 0 && statsInfoList[0].stats[CalcType.TOTAL] !== NA) {
		if (statsInfoList.length === 2) {
			diff = statsInfoList[0].statsUse[CalcType.TOTAL] - statsInfoList[1].statsUse[CalcType.TOTAL];
			diff = _round(diff);

			_setSummableToolTips(toolTipStr, statsInfoList, getTooltip, formatSpec);
		}
		statsInfoList.forEach((infoForChart) => {
			total += infoForChart.statsUse[CalcType.TOTAL];
			perAllData += infoForChart.statsUse[CalcType.PERCENT_vsALLDATA];
		});
	}

	let totalSel = 0;
	let diffSel = undefined;

	if (statsInfoList && statsInfoList.length > 0 && statsInfoList[0].stats[CalcType.TOTAL_SEL] !== NA) {
		if (statsInfoList.length === 2) {
			diffSel = statsInfoList[0].statsUse[CalcType.TOTAL_SEL] - statsInfoList[1].statsUse[CalcType.TOTAL_SEL];
			diffSel = _round(diffSel);
			_setSummableToolTips(toolTipStr, statsInfoList, getTooltip, formatSpec, true);
		}

		statsInfoList.forEach((infoForChart) => {
			totalSel += infoForChart.statsUse[CalcType.TOTAL_SEL];
		});
	}

	let roundedTotal = _round(total);
	result[CalcType.TOTAL] = formatSpec ? _formatNumber(roundedTotal, formatSpec) : roundedTotal.toString();
	resultUse[CalcType.TOTAL] = roundedTotal;
	result[CalcType.DIFF] = formatSpec ? _formatNumber(diff, formatSpec) : diff && diff.toString() || NA;
	resultUse[CalcType.DIFF] = diff;
	result[CalcType.PERCENT_vsALLDATA] = _percentString(_round(perAllData));
	resultUse[CalcType.PERCENT_vsALLDATA] = _round(perAllData);

	roundedTotal = _round(totalSel);
	result[CalcType.TOTAL_SEL] = formatSpec ? _formatNumber(roundedTotal, formatSpec) : roundedTotal.toString();
	resultUse[CalcType.TOTAL_SEL] = roundedTotal;
	result[CalcType.DIFF_SEL] = formatSpec ? _formatNumber(diffSel, formatSpec) : diffSel && diffSel.toString() || NA;
	resultUse[CalcType.DIFF_SEL] = diffSel || NA;

	result[CalcType.PERCENT_CHARTvsCHART] = _percentString(100);
	resultUse[CalcType.PERCENT_CHARTvsCHART] = 100;
	toolTipStr[CalcType.PERCENT_CHARTvsCHART] = undefined;

	toolTip[CalcType.TOTAL] = toolTipStr[CalcType.TOTAL];
	toolTip[CalcType.DIFF] = toolTipStr[CalcType.DIFF];
	toolTip[CalcType.TOTAL_SEL] = toolTipStr[CalcType.TOTAL_SEL];
	toolTip[CalcType.DIFF_SEL] = toolTipStr[CalcType.DIFF_SEL];
	toolTip[CalcType.PERCENT_vsALLDATA] = toolTipStr[CalcType.PERCENT_vsALLDATA];
	toolTip[CalcType.PERCENT_CHARTvsCHART] = toolTipStr[CalcType.PERCENT_CHARTvsCHART];

	return { stats: result, statsUse: resultUse, toolTip };
}

function _computeAverageData(statsInfoList, prop, getSingleTooltip, /*optional*/ stats) {
	const result = {};
	const resultUse = {};
	const toolTip = {};
	let combinedAverage = NA;

	if (statsInfoList && statsInfoList.length === 2) {
		const formatSpec = statsInfoList[0].meta && statsInfoList[0].meta.format;
		[CalcType.AVG, CalcType.AVG_SEL].forEach((calcType) => {
			if (statsInfoList[0].stats[calcType] !== NA && statsInfoList[1].stats[calcType] !== NA) {
				let chartA_rowCount = 0, chartB_rowCount = 0;
				const chartA_total = statsInfoList[0].statsUse[CalcType.TOTAL];
				const chartB_total = statsInfoList[1].statsUse[CalcType.TOTAL];
				const chartA_total_sel = statsInfoList[0].statsUse[CalcType.TOTAL_SEL];
				const chartB_total_sel = statsInfoList[1].statsUse[CalcType.TOTAL_SEL];
				const chartA_missingValueCount = statsInfoList[0].statsUse[CalcType.MISSING_VALUE_COUNT] || 0;
				const chartB_missingValueCount = statsInfoList[1].statsUse[CalcType.MISSING_VALUE_COUNT] || 0;
				if ((typeof chartA_total === 'number') && (typeof chartB_total === 'number')) { // combined averages for summary
					chartA_rowCount = statsInfoList[0].statsUse[CalcType.ROW_COUNT];
					chartB_rowCount = statsInfoList[1].statsUse[CalcType.ROW_COUNT];
					combinedAverage = _round((chartA_total + chartB_total) / (chartA_rowCount + chartB_rowCount));
				} else if ((typeof chartA_total_sel === 'number') && (typeof chartB_total_sel === 'number')) { // combined averages for selection
					chartA_rowCount = statsInfoList[0].statsUse[CalcType.NUM_SELECTIONS];
					chartB_rowCount = statsInfoList[1].statsUse[CalcType.NUM_SELECTIONS];
					combinedAverage = _round((chartA_total_sel + chartB_total_sel) / (chartA_rowCount + chartB_rowCount));
				}
				result[calcType] = formatSpec ? _formatNumber(combinedAverage, formatSpec) : combinedAverage.toString();
				resultUse[calcType] = combinedAverage;

				if (getSingleTooltip && combinedAverage && (chartA_missingValueCount + chartB_missingValueCount)) {
					toolTip[calcType] = getSingleTooltip(CalcType.MISSING_VALUE_COUNT, { avg: combinedAverage, rowCount: chartA_rowCount + chartB_rowCount, missingValueCount: chartA_missingValueCount + chartB_missingValueCount });
				} else if (getSingleTooltip && combinedAverage) {
					toolTip[calcType] = getSingleTooltip(calcType, { avg: combinedAverage, rowCount: chartA_rowCount + chartB_rowCount });
				}
			}
		});
	}

	if (stats) {
		stats.stats = { ...stats.stats, ...result };
		stats.statsUse = { ...stats.statsUse, ...resultUse };
		stats.toolTip = { ...stats.toolTip, ...toolTip };
	} else {
		stats = { stats: result, statsUse: resultUse, toolTip };
	}

	return stats;
}

function _computeWeightedAverageData(statsInfoList, prop, getSingleTooltip) {
	const result = {};
	const resultUse = {};
	const toolTip = {};

	if (statsInfoList && statsInfoList.length === 2) {
		const formatSpec = statsInfoList[0].meta && statsInfoList[0].meta.format;
		[CalcType.WEIGHTED_AVG, CalcType.WEIGHTED_AVG_SEL].forEach((calcType) => {
			if (statsInfoList[0].stats[calcType] !== NA && statsInfoList[1].stats[calcType] !== NA) {
				const detailRowCount0 = statsInfoList[0].statsUse[CalcType.DETAIL_ROWCOUNT];
				const detailRowCount1 = statsInfoList[1].statsUse[CalcType.DETAIL_ROWCOUNT];
				if (detailRowCount0 > 0 && detailRowCount1 > 0) {
					const detailSum0 = statsInfoList[0].statsUse[CalcType.DETAIL_SUM];
					const detailSum1 = statsInfoList[1].statsUse[CalcType.DETAIL_SUM];
					const weightedAverage = _round((detailSum0 + detailSum1) / (detailRowCount0 + detailRowCount1));
					result[calcType] = formatSpec ? _formatNumber(weightedAverage, formatSpec) : weightedAverage.toString();
					resultUse[calcType] = weightedAverage;

					if (getSingleTooltip && weightedAverage) {
						toolTip[calcType] = getSingleTooltip(calcType, { weightedAvg: weightedAverage, detailRowCount: detailRowCount0 + detailRowCount1 });
					}
				}
			}
		});
	}

	return { stats: result, statsUse: resultUse, toolTip };
}

function _computeDiffData(statsInfoList, prop, propSel, getTooltip, multiAllowed = false) {
	const result = {};
	const toolTip = {};
	const toolTipStr = {};
	const formatSpec = statsInfoList[0].meta && statsInfoList[0].meta.format;

	if (statsInfoList && statsInfoList.length > 0) {
		if (statsInfoList[0].stats[CalcType.DIFF] !== NA) {

			if (statsInfoList.length === 2) {
				result[CalcType.DIFF] = statsInfoList[0].statsUse[prop] - statsInfoList[1].statsUse[prop];

				_setDiffToolTips(toolTipStr, prop, statsInfoList, getTooltip, formatSpec);
				toolTip[CalcType.DIFF] = toolTipStr[CalcType.DIFF];
			}
		}

		if (statsInfoList[0].stats[CalcType.DIFF_SEL] !== NA) {
			if (statsInfoList.length === 2) {
				if (multiAllowed || (statsInfoList[0].statsUse[CalcType.NUM_SELECTIONS] === 1 &&
					statsInfoList[1].statsUse[CalcType.NUM_SELECTIONS] === 1)) {
					const val = statsInfoList[0].statsUse[propSel] - statsInfoList[1].statsUse[propSel];
					result[CalcType.DIFF_SEL] = _round(val);
					_setDiffToolTips(toolTipStr, propSel, statsInfoList, getTooltip, formatSpec, true);
					toolTip[CalcType.DIFF_SEL] = toolTipStr[CalcType.DIFF_SEL];
				}
			}
		}
	}

	return { stats: result, statsUse: result, toolTip };
}

function _computeCombinedSummaryValues(measureInfo, totalCalc, percentOfDatasetCalc, overallSummaryValue, getSingleTooltip, stats = null) {
	const result = {};
	const resultUse = {};
	const toolTip = {};

	if (measureInfo) {
		result[totalCalc] = measureInfo.filteredSummaryValue;
		resultUse[totalCalc] = measureInfo.filteredSummaryValueUse;
		const percentDatasetCalc = _round(measureInfo.filteredSummaryValueUse / overallSummaryValue * 100);
		result[percentOfDatasetCalc] = _percentString(percentDatasetCalc);
		resultUse[percentOfDatasetCalc] = percentDatasetCalc;
		if (getSingleTooltip && measureInfo.filteredSummaryValue) {
			const label = measureInfo.metadata && measureInfo.metadata.label || NA; //NA should never happen.
			toolTip[totalCalc] = getSingleTooltip('countdistinct_' + totalCalc, { uniqueValues: measureInfo.filteredSummaryValue, label: label, overallSummaryValue: overallSummaryValue });
			toolTip[percentOfDatasetCalc] = getSingleTooltip('countdistinct_' + percentOfDatasetCalc, { uniqueValues: measureInfo.filteredSummaryValue, label: label, percent: result[percentOfDatasetCalc], overallSummaryValue: overallSummaryValue });
		}
	}

	if (stats) {
		stats.stats = { ...stats.stats, ...result };
		stats.statsUse = { ...stats.statsUse, ...resultUse };
		stats.toolTip = { ...stats.toolTip, ...toolTip };
	} else {
		stats = { stats: result, statsuse: resultUse, toolTip };
	}
	return stats;
}


/***********************
 * Applicable calculations for default and selection case
 ***********************/

function getApplicableSummaryCalcTypes(measures, newCompareTableStatsEnabled) {
	let stats = [];

	if (measures.length === 0) {
		return [];
	} else if (_checkAggregateType(measures, _isSummable)) {
		stats.push(CalcType.PERCENT_vsALLDATA);
		if (_areAllMeasuresSame(measures)) {
			stats.push(CalcType.PERCENT_CHARTvsCHART);
			_addAverage(stats);
			stats = stats.concat([CalcType.TOTAL, CalcType.DIFF]);
		} else {
			_addAverage(stats);
			stats.push(CalcType.TOTAL);
		}
	} else if (_checkAggregateType(measures, _isMinMax)) {
		stats = [CalcType.MIN, CalcType.MAX];
		_addAverage(stats);
	} else if (_checkAggregateType(measures, _isAvg)) {
		stats = [CalcType.MIN, CalcType.MAX, CalcType.WEIGHTED_AVG];
	} else if (_checkAggregateType(measures, _isCountDistinct)) {
		if (newCompareTableStatsEnabled) {
			stats = [CalcType.MIN, CalcType.MAX, CalcType.PERCENT_vsALLDATA, CalcType.TOTAL, CalcType.DIFF];
		} else {
			stats = [CalcType.MIN, CalcType.MAX];
		}
	} else {
		stats = [CalcType.MIN, CalcType.MAX];

		_addAverage(stats, measures);

		if (_atleastOneAggType(measures, _isAvg)) {
			stats.push(CalcType.WEIGHTED_AVG);
		}

		if (_atleastOneAggType(measures, _isSummable)) {
			stats = stats.concat([CalcType.PERCENT_vsALLDATA, CalcType.TOTAL]);
		}
	}

	return stats;
}

function getApplicableSelectionSummaryCalcTypes(measures, newCompareTableStatsEnabled) {
	let stats = [];

	if (measures.length === 0) {
		return [];
	} else if (_checkAggregateType(measures, _isSummable)) {
		stats.push(CalcType.PERCENT_SEL_vsALLDATA);
		if (_areAllMeasuresSame(measures)) {
			stats.push(CalcType.PERCENT_SELvsCHART);
			_addAverageSel(stats);
			stats = stats.concat([CalcType.TOTAL_SEL, CalcType.DIFF_SEL]);
		} else {
			_addAverageSel(stats);
			stats.push(CalcType.TOTAL_SEL);
		}
	} else if (_checkAggregateType(measures, _isAvg)) {
		stats = [CalcType.MIN_SEL, CalcType.MAX_SEL, CalcType.WEIGHTED_AVG_SEL];

	} else if (_checkAggregateType(measures, _isMax) || _checkAggregateType(measures, _isMin)) {
		stats = [CalcType.MIN_SEL, CalcType.MAX_SEL, CalcType.DIFF_SEL];
		_addAverageSel(stats);
	} else if (_checkAggregateType(measures, _isCountDistinct)) {
		if (newCompareTableStatsEnabled) {
			stats = [CalcType.MIN_SEL, CalcType.MAX_SEL, CalcType.PERCENT_SEL_vsALLDATA, CalcType.TOTAL_SEL, CalcType.DIFF_SEL];
		} else {
			stats = [CalcType.MIN_SEL, CalcType.MAX_SEL];
		}
	} else {
		stats = [CalcType.MIN_SEL, CalcType.MAX_SEL];

		_addAverageSel(stats, measures);
		if (_atleastOneAggType(measures, _isAvg)) {
			stats.push(CalcType.WEIGHTED_AVG_SEL);
		}
		if (_atleastOneAggType(measures, _isSummable)) {
			stats = stats.concat([CalcType.PERCENT_SEL_vsALLDATA, CalcType.TOTAL_SEL]);
		}
	}

	return stats;
}

function _getMeasureAggregateInfo(measuresMap, nameOfValueProperty) {
	measuresMap = measuresMap || new Map();
	const cumulativeTotalCollection = {};

	measuresMap.forEach((measureDetail, measureName) => {
		const meta = measureDetail.metadata || measureDetail.get('metadata');

		if (meta && _isSummable(meta.aggregationType)) {

			const value = measureDetail[nameOfValueProperty] || measureDetail.get(nameOfValueProperty);

			const _value = _getMeasureValue(meta.aggregationType, value);
			const item = {
				measureLabel: meta.label,
				meta: meta
			};

			cumulativeTotalCollection[measureName] = cumulativeTotalCollection[measureName] || {};
			cumulativeTotalCollection[measureName].value = cumulativeTotalCollection[measureName].value || 0;
			cumulativeTotalCollection[measureName].value += _value;
			cumulativeTotalCollection[measureName].detail = cumulativeTotalCollection[measureName].detail || item;
		}
	});

	return cumulativeTotalCollection;
}

/*
* @return {Map.<Object>} Object is of the form
* {
*   <widgetId> : {
*       <measureName>: {
*           value : <aggregate % number>,
*           allChartTotal: <number>,
*           measureLabel : <string>
*       }
*   }
* }
*/
function getChartVsChartTotalPercentStats(statsModelMap) {

	const { cumulativeAggregate, chartInfo } = _getChartAggregate(statsModelMap);

	return _getPercentTotalChart(cumulativeAggregate, chartInfo);
}

function _getChartAggregate(statsModelMap) {
	const cumulativeAggregate = {};
	const chartInfo = {};
	Array.from(statsModelMap.entries()).forEach((entry) => {
		const widgetId = entry[0];
		const statsModel = entry[1];
		const widgetAggregateInfo = _getMeasureAggregateInfo(statsModel.measures, 'totalUse');
		chartInfo[widgetId] = widgetAggregateInfo;
		_addTo(widgetAggregateInfo, cumulativeAggregate);
	});

	return { cumulativeAggregate, chartInfo };
}

function _addTo(widgetAggregateInfo, cumulativeAggregate) {
	Object.entries(widgetAggregateInfo).map((measureEntry) => {
		const measureName = measureEntry[0];
		const measureInfo = measureEntry[1];
		const meta = measureInfo.detail.meta;

		if (_isSummable(meta.aggregationType)) {
			cumulativeAggregate[measureName] = cumulativeAggregate[measureName] || 0;
			cumulativeAggregate[measureName] += measureInfo.value;
		}
		return cumulativeAggregate;
	});
}

function _getPercentTotalChart(cumulativeAggregate, chartInfo) {

	const percentStats = {};
	Object.entries(chartInfo).forEach((entry) => {
		const widgetId = entry[0];
		const widgetInfo = entry[1];
		Object.entries(widgetInfo).forEach((measureEntry) => {
			const measureName = measureEntry[0];
			const measureValue = measureEntry[1].value;
			const measureLabel = measureEntry[1].detail.measureLabel;

			const cumulativeTotal = cumulativeAggregate[measureName];

			percentStats[widgetId] = percentStats[widgetId] || {};
			percentStats[widgetId][measureName] = percentStats[widgetId][measureName] || {};
			percentStats[widgetId][measureName].value = _round(measureValue / cumulativeTotal * 100);
			percentStats[widgetId][measureName].measureLabel = percentStats[widgetId][measureName].measureLabel || measureLabel;
			percentStats[widgetId][measureName].allChartTotal = cumulativeTotal; //for tooltip
		});
	});
	return percentStats;
}

export default {
	getChartSummaryStats,
	getChartSelectionSummaryStats,
	getSelectionContextLabel,
	getCalculatedSummaryData,
	getApplicableSummaryCalcTypes,
	getApplicableSelectionSummaryCalcTypes,
	getChartVsChartTotalPercentStats
};


// WEBPACK FOOTER //
// ./src/js/features/compare/StatsProcessor.js